import React, { useState } from "react";
import { fireEvent, render, screen } from "@testing-library/react";
import "@testing-library/jest-dom";
import { ActiveFilters, buildFilterChips } from "./ActiveFilters";
import type { FilterInterface } from "./SearchFilters";
import type { FilterValues } from "./filterState";

const COLOUR = "value.group_a.colour.value";
const SIZE = "value.check_size_usd.value";
const DATE = "value.date";

const schema: FilterInterface = {
  path: "value",
  children: [
    {
      path: "value.group_a",
      children: [
        {
          path: COLOUR,
          type: "string",
          allowed_values: ["Red", "Green", "Blue"],
        },
      ],
    },
    { path: SIZE, type: "number", min: 0, max: 1_000_000_000 },
    { path: DATE, type: "date", min: "2020-01-01", max: "2026-01-01" },
  ],
};

const colours = (...picked: string[]): FilterValues => ({
  [COLOUR]: { path: COLOUR, type: "string", allowed_values: picked },
});

const chipText = (values: FilterValues) =>
  buildFilterChips(schema, values).map((chip) => [chip.label, chip.value]);

describe("buildFilterChips", () => {
  it("gives each selected value its own chip, labelled with its group", () => {
    expect(chipText(colours("Red", "Green"))).toEqual([
      ["Group a › Colour", "Red"],
      ["Group a › Colour", "Green"],
    ]);
  });

  it("shows typed text as a contains chip, removable on its own", () => {
    const values: FilterValues = {
      [COLOUR]: {
        path: COLOUR,
        type: "string",
        allowed_values: ["Red"],
        contains: ["gre"],
      },
    };

    const chips = buildFilterChips(schema, values);
    expect(chips.map((chip) => chip.value)).toEqual(["Red", "contains “gre”"]);
    expect(chips[1].next).toEqual(colours("Red"));
    expect(chips[0].next).toEqual({
      [COLOUR]: {
        path: COLOUR,
        type: "string",
        allowed_values: [],
        contains: ["gre"],
      },
    });
  });

  it("describes ranges and dates by their bounds", () => {
    expect(
      chipText({
        [SIZE]: { path: SIZE, type: "number", min: 5_000_000, max: 20_000_000 },
        [DATE]: { path: DATE, type: "date", min: "2024-03-01" },
      }),
    ).toEqual([
      ["Check Size (USD)", "$5M – $20M"],
      ["Event Date", "from 2024-03-01"],
    ]);
  });

  it("removes just that value, or the whole filter with its last value", () => {
    const [red] = buildFilterChips(schema, colours("Red", "Green"));
    expect(red.next).toEqual(colours("Green"));

    const [only] = buildFilterChips(schema, colours("Green"));
    expect(only.next).toEqual({});
  });
});

const Harness = ({ initial }: { initial: FilterValues }) => {
  const [values, setValues] = useState<FilterValues>(initial);
  return <ActiveFilters schema={schema} values={values} onChange={setValues} />;
};

describe("ActiveFilters", () => {
  it("renders nothing when no filter is active", () => {
    render(<ActiveFilters schema={schema} values={{}} onChange={() => {}} />);
    expect(
      screen.queryByRole("group", { name: "Active filters" }),
    ).not.toBeInTheDocument();
  });

  it("removes one value from its chip and keeps the rest", () => {
    render(<Harness initial={colours("Red", "Green")} />);

    fireEvent.click(
      screen.getByRole("button", { name: "Remove Group a › Colour: Red" }),
    );

    expect(
      screen.queryByRole("button", { name: "Remove Group a › Colour: Red" }),
    ).not.toBeInTheDocument();
    expect(
      screen.getByRole("button", { name: "Remove Group a › Colour: Green" }),
    ).toBeInTheDocument();
  });

  it("clears every filter at once", () => {
    const onChange = jest.fn();
    render(
      <ActiveFilters
        schema={schema}
        values={colours("Red")}
        onChange={onChange}
      />,
    );

    fireEvent.click(screen.getByRole("button", { name: "Clear all" }));

    expect(onChange).toHaveBeenCalledWith({});
  });
});
